import { type PageTourStep } from '@apps/web/components/page_tour/page_tour';
import {
    PageTourReplayButton,
    PageTourScope,
} from '@apps/web/components/page_tour/page_tour_scope';
import {
    getPageTourSeenStorageKey,
    getTrainingPageTourScope,
} from '@apps/web/components/page_tour/page_tour_storage';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * First-visit guided walkthrough for a page while the organization is in
 * training mode. Wrap page content with this scope and place
 * `TrainingModePageTourReplayButton` in the page header.
 */
export function TrainingModePageTourScope({
    pageId,
    organizationId,
    ready,
    steps,
    children,
}: {
    readonly pageId: string;
    readonly organizationId: string | null;
    readonly ready: boolean;
    readonly steps: ReadonlyArray<PageTourStep>;
    readonly children: React.ReactNode;
}) {
    const { data: user } = useUser({
        reason: `training-page-tour:${pageId}`,
    });
    const { isReady: trainingReady, isInTrainingMode } = useTrainingMode({
        organizationId,
        reason: `training-page-tour:${pageId}`,
    });
    const userId = user?.id ?? null;
    const seenStorageKey =
        userId !== null && organizationId !== null
            ? getPageTourSeenStorageKey(
                  getTrainingPageTourScope(pageId, organizationId),
                  userId,
              )
            : null;
    const active = trainingReady && isInTrainingMode;

    return (
        <PageTourScope
            enabled={active}
            showReplayButton={active}
            ready={ready && trainingReady}
            userId={userId}
            seenStorageKey={seenStorageKey}
            steps={steps}
            testIdPrefix={`training-tour-${pageId}`}
        >
            {children}
        </PageTourScope>
    );
}

export { PageTourReplayButton as TrainingModePageTourReplayButton };
